import type { ApiClient, Schemas } from '../generated/laravel-typed-openapi';

/**
 * The signed-in user's favorites over REST — `GET`, `POST` and `DELETE /favorites`.
 *
 * Replaces two parallel implementations that both went dark when public boats moved to Sanity: the
 * Strapi CMS routes under `/api/user-favorite-boat*`, and the Lumen `addToFavorite` /
 * `removeFromFavorite` mutations. Both keyed favorites on a numeric boat id; a favorite is keyed on
 * a Sanity document id now, which is what `favoritable_ref` carries.
 *
 * These four functions are the only place the generated client is touched. Everything above them
 * works in `Schemas.FavoriteResource`.
 */

export type Favorite = Schemas.FavoriteResource;
export type FavoritableType = Schemas.FavoritableType;

/** The only kind of thing that can be favorited today. */
export const BOAT: FavoritableType = 'boat';

type FetchOptions = {
  signal?: AbortSignal;
};

const withSignal = ({ signal }: FetchOptions) => (signal ? { overrides: { signal } } : {});

/**
 * Every favorite of the given kind, newest first.
 *
 * Unpaginated by design — see the endpoint's own notes. The caller needs the whole set to answer
 * "is this card favorited?" without a request per card.
 */
export const fetchFavorites = async (
  apiClient: ApiClient,
  favoritableType: FavoritableType,
  options: FetchOptions = {},
): Promise<Favorite[]> =>
  await apiClient.get('/favorites', {
    query: { favoritable_type: favoritableType },
    ...withSignal(options),
  });

/**
 * Favorite something, and return the row the server settled on.
 *
 * `uuid` is sent rather than left to the server so the caller names the row it is creating. That is
 * what makes a retry safe — the endpoint resolves a uuid it has already seen instead of creating a
 * second favorite — and it is what lets a caller put the row in its cache before the request
 * answers. The response is still the authority: favoriting something already favorited returns the
 * existing row, whose `uuid` is not the one that was sent.
 */
export const addFavorite = async (
  apiClient: ApiClient,
  favoritableRef: string,
  favoritableType: FavoritableType = BOAT,
  uuid: string = crypto.randomUUID(),
): Promise<Favorite> =>
  await apiClient.post('/favorites', {
    body: { uuid, favoritable_type: favoritableType, favoritable_ref: favoritableRef },
  });

/**
 * Remove a favorite, by the favorite's own uuid — not the favorited resource's.
 *
 * Idempotent server-side: a uuid that names nothing reports success rather than 404, so a double
 * click or a retry settles instead of surfacing an error the user cannot act on.
 */
export const removeFavorite = async (apiClient: ApiClient, uuid: string): Promise<void> => {
  await apiClient.delete('/favorites/{uuid}', { path: { uuid } });
};
